import React from 'react';
import { motion } from 'framer-motion';
import { FaUserCircle, FaSearch, FaBell, FaCog, FaCheckCircle, FaExclamationCircle, FaArrowUp, FaArrowDown, FaChartLine, FaWallet, FaRegEdit } from 'react-icons/fa';
import { FaArrowTrendUp } from 'react-icons/fa6';
import { useTranslation, useGo } from '@refinedev/core';
import dayjs from 'dayjs';

interface Invoice {
    id: string;
    amount: number;
    updated_at?: string;
    status?: string;
}

export interface ConfigDashboardLayoutProps {
    user: any;
    identity: any;
    planOrMembershipLabel: string;
    planOrMembershipValue: string;
    balance: string | number;
    invoices: Invoice[];
    googleAuthenticated: boolean;
    onGoogleConnect: () => void;
    onLogout: () => void;
    fullName: string;
}

export const ConfigDashboardLayout: React.FC<ConfigDashboardLayoutProps> = ({
    user,
    identity,
    planOrMembershipLabel,
    planOrMembershipValue,
    balance,
    invoices,
    googleAuthenticated,
    onGoogleConnect,
    onLogout,
    fullName
}) => {
    const { translate } = useTranslation();
    const go = useGo();

    const statsCards = [
        {
            title: planOrMembershipLabel,
            value: planOrMembershipValue || 'N/A',
            badge: `+${translate("page.config.ready")}`,
            chart: (
                <svg className="w-16 h-8 text-white/50" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 30 Q 20 10, 40 25 T 80 15 T 100 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            ),
        },
        {
            title: translate("common.google.title"),
            value: googleAuthenticated ? translate("page.config.connected") : translate("page.config.pending"),
            badge: googleAuthenticated ? `+${translate("page.config.verified")}` : translate("page.config.action-required"),
            chart: (
                <svg className="w-16 h-8 text-white/50" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 25 Q 15 5, 30 20 T 65 15 T 100 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            ),
        },
        {
            title: translate("page.config.requests"),
            value: "0/0",
            badge: `+${translate("page.config.ready")}`,
            chart: (
                <svg className="w-16 h-8 text-white/50" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 20 Q 25 35, 50 15 T 100 25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            ),
        }
    ];

    return (
        <div className="flex flex-col xl:flex-row gap-6 p-4 md:p-8 w-full min-h-full bg-base-200 rounded-box">

            {/* Left Main Section */}
            <div className="flex-1 flex flex-col gap-6">

                {/* Header (Dashboard Title & Search) */}
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-secondary m-0">{translate("page.config.dashboard-title")}</h1>
                        <p className="text-sm text-neutral-400 m-0 mt-1">{translate("page.config.dashboard-subtitle")}</p>
                    </div>
                    <div className="relative w-full sm:w-auto">
                        <input
                            type="text"
                            placeholder={translate("page.config.search-placeholder")}
                            className="input input-bordered w-full sm:w-64 rounded-full pl-5 pr-10 bg-white dark:bg-base-200 border-neutral-200 dark:border-neutral-700 shadow-sm focus:outline-none"
                        />
                        <FaSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    </div>
                </header>

                {/* Top Green Stat Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {statsCards.map((stat, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * idx, duration: 0.5 }}
                            className="bg-gradient-to-r from-primary to-primary-focus rounded-3xl p-5 flex flex-col justify-between shadow-lg shadow-primary/20 text-primary-content relative overflow-hidden"
                        >
                            <div className="flex justify-between items-start relative z-10">
                                <div className="flex flex-col">
                                    <span className="text-primary-content/90 text-sm mb-1">{stat.title}</span>
                                    <span className="text-2xl font-bold tracking-tight">{stat.value}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-end mt-6 relative z-10 w-full">
                                <div className="bg-primary-content/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                    <FaArrowTrendUp className="w-3 h-3" />
                                    {stat.badge}
                                </div>
                                <div className="absolute right-0 bottom-0">
                                    {stat.chart}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Middle Charts Placeholders */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Revenue Line Chart */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                        className="lg:col-span-2 card bg-base-100 dark:bg-base-200 shadow-sm border border-base-200 dark:border-neutral flex flex-col relative"
                    >
                        <div className="card-body p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="card-title text-lg text-base-content">{translate("page.config.activity-title")}</h3>
                                <div className="flex gap-4 text-xs font-semibold">
                                    <span className="flex items-center gap-1 text-primary tracking-wide"><div className="w-2 h-2 rounded-full bg-primary"></div> {translate("page.config.connected")}</span>
                                    <span className="flex items-center gap-1 text-orange-400 tracking-wide"><div className="w-2 h-2 rounded-full bg-orange-400"></div> {translate("page.config.pending")}</span>
                                </div>
                            </div>
                            {/* CSS Chart Graphic Placeholder */}
                            <div className="flex-1 min-h-[180px] w-full relative flex items-end ml-4">
                                {/* Y Axis labels */}
                                <div className="absolute left-[-20px] top-0 bottom-6 flex flex-col justify-between text-[10px] text-neutral-600 dark:text-neutral-400 font-medium">
                                    <span>100%</span>
                                    <span>75%</span>
                                    <span>50%</span>
                                    <span>25%</span>
                                    <span>0</span>
                                </div>
                                {/* Grid lines */}
                                <div className="absolute left-4 right-0 top-1 bottom-6 flex flex-col justify-between">
                                    <div className="border-b border-neutral-100 dark:border-neutral-800 w-full"></div>
                                    <div className="border-b border-neutral-100 dark:border-neutral-800 w-full"></div>
                                    <div className="border-b border-neutral-100 dark:border-neutral-800 w-full"></div>
                                    <div className="border-b border-neutral-100 dark:border-neutral-800 w-full"></div>
                                    <div className="border-b border-neutral-200 dark:border-neutral-700 w-full"></div>
                                </div>

                                {/* Mock SVG Line */}
                                <svg className="absolute left-4 right-0 top-0 bottom-6 w-[calc(100%-1rem)] h-full overflow-visible" preserveAspectRatio="none">
                                    {/* Gradient fill under curve */}
                                    <defs>
                                        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="oklch(var(--p))" stopOpacity="0.2" />
                                            <stop offset="100%" stopColor="oklch(var(--p))" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <path d="M0 80 Q 50 120, 100 60 T 200 80 T 300 30 T 400 60 T 500 40 L 500 150 L 0 150 Z" fill="url(#chartGradient)" vectorEffect="non-scaling-stroke" />
                                    <path d="M0 80 Q 50 120, 100 60 T 200 80 T 300 30 T 400 60 T 500 40" fill="none" stroke="oklch(var(--p))" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinecap="round" />

                                    {/* Data Point dot */}
                                    <circle cx="300" cy="30" r="4" fill="#ffffff" stroke="oklch(var(--p))" strokeWidth="2" />
                                </svg>
                                {/* Tooltip on dot */}
                                <div className="absolute left-[calc(60%-2rem)] top-[10%] bg-primary text-white text-xs font-bold px-2 py-1 rounded shadow-md pointer-events-none">
                                    {translate("page.config.optimal")}
                                </div>
                                {/* X Axis */}
                                <div className="absolute -bottom-1 left-4 right-0 flex justify-between text-[11px] text-neutral-600 dark:text-neutral-400 font-medium px-2">
                                    <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Customers Donut Chart */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                        className="card bg-base-100 dark:bg-base-200 shadow-sm border border-base-200 dark:border-neutral flex flex-col items-center justify-between"
                    >
                        <div className="card-body p-6 items-center w-full">
                            <h3 className="card-title text-lg text-base-content self-start mb-2">{translate("page.config.completion-title")}</h3>
                            <div className="relative w-40 h-40 flex items-center justify-center">
                                {/* SVG Donut */}
                                <svg className="w-full h-full transform -rotate-90">
                                    {/* Gray Track */}
                                    <circle cx="80" cy="80" r="60" fill="transparent" stroke="currentColor" strokeWidth="16" className="text-neutral-100 dark:text-neutral-800" />
                                    {/* Green/Orange Progress (Multiple segments) */}
                                    <circle cx="80" cy="80" r="60" fill="transparent" stroke="oklch(var(--p))" strokeWidth="16" strokeDasharray="300 100" className="drop-shadow-sm" />
                                    <circle cx="80" cy="80" r="60" fill="transparent" stroke="#fbd38d" strokeWidth="16" strokeDasharray="60 376" strokeDashoffset="-320" />
                                    {/* Inner Text */}
                                </svg>
                                <div className="absolute flex flex-col items-center justify-center">
                                    <span className="font-bold text-3xl text-neutral-800 dark:text-white">85%</span>
                                    <span className="text-xs text-neutral-600 dark:text-neutral-400 font-medium tracking-wide">{translate("page.config.ready")}</span>
                                </div>
                            </div>
                            <div className="flex gap-4 text-xs font-bold mt-4">
                                <span className="flex items-center gap-1 text-primary"><div className="w-2 h-2 rounded-full bg-primary"></div> {translate("page.config.setup-done")}</span>
                                <span className="flex items-center gap-1 text-orange-400"><div className="w-2 h-2 rounded-full bg-orange-400"></div> {translate("page.config.missing")}</span>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Recent Transactions Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="card bg-base-100 dark:bg-base-200 shadow-sm border border-base-200 dark:border-neutral"
                >
                    <div className="card-body p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="card-title text-lg text-base-content">{translate("page.config.recent-transactions")}</h3>
                            <button className="btn btn-ghost btn-xs text-neutral-500 hover:text-base-content transition-colors">{translate("page.config.view-all")}</button>
                        </div>

                        <div className="overflow-x-auto w-full pb-2 scrollbar-thin">
                            <table className="w-full text-left border-collapse min-w-[500px]">
                                <thead>
                                    <tr className="text-xs font-bold text-neutral-600 dark:text-neutral-400 uppercase tracking-wider border-b border-neutral-100 dark:border-neutral-800">
                                        <th className="font-medium pb-3 pl-2">{translate("page.config.transaction-id")}</th>
                                        <th className="font-medium pb-3">{translate("page.config.date")}</th>
                                        <th className="font-medium pb-3">{translate("page.config.amount")}</th>
                                        <th className="font-medium pb-3">{translate("page.config.status-label")}</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium">
                                    {invoices && invoices.length > 0 ? invoices.map((invoice, i) => (
                                        <tr key={invoice.id} className="border-b border-neutral-50 dark:border-neutral-800/50 hover:bg-neutral-50 dark:hover:bg-neutral-800/20 transition-colors group">
                                            <td className="py-4 pl-2 flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                                                    <FaArrowUp className="w-3 h-3" />
                                                </div>
                                                <span className="text-neutral-600 dark:text-neutral-300">#{invoice.id.split('-')[0].substring(0, 8)}</span>
                                            </td>
                                            <td className="py-4 text-neutral-500 dark:text-neutral-400">
                                                {invoice.updated_at ? dayjs(invoice.updated_at).format('MMM DD, YYYY, HH:mm') : 'N/A'}
                                            </td>
                                            <td className="py-4 text-neutral-800 dark:text-neutral-200 font-bold">
                                                ${invoice.amount}
                                            </td>
                                            <td className="py-4">
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                                                    {invoice.status ?? 'Completed'}
                                                </span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} className="text-center py-8 text-neutral-400">{translate("page.config.no-transactions")}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="md:hidden mt-6">
                        <button className='btn btn-ghost text-red-500 hover:bg-red-50 hover:text-red-600 w-full rounded-2xl' onClick={() => onLogout()}>
                            <FaCog className="mr-2" />{translate("common.logout")}
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* Right Sidebar (Profile & Earning) */}
            <div className="xl:w-[340px] w-full flex flex-col gap-6">

                {/* Header Actions */}
                <div className="hidden xl:flex justify-end gap-3 mb-2">
                    <button className="btn btn-circle btn-sm bg-base-100 dark:bg-base-200 border-base-200 text-base-content hover:text-primary relative">
                        <FaBell />
                        <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full border border-base-100"></span>
                    </button>
                    <button className="btn btn-circle btn-sm bg-base-100 dark:bg-base-200 border-base-200 text-base-content hover:text-primary" onClick={() => go({ to: '/config/pqrs' })}>
                        <FaCog />
                    </button>
                </div>

                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="card bg-neutral text-neutral-content rounded-box shadow-xl relative overflow-hidden flex flex-col items-center p-5"
                >
                    {/* Header Banner Graph */}
                    <div className="w-full h-32 bg-primary rounded-box relative overflow-hidden mb-12">
                        {/* Abstract Shapes inside banner */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 transform rotate-45 translate-x-10 -translate-y-10"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 transform rotate-12 -translate-x-5 translate-y-5"></div>
                        <button className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/20 p-2 rounded-full cursor-pointer z-10">
                            <FaCog className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Avatar Overlapping Menu */}
                    <div className="absolute top-28 w-24 h-24 rounded-full border-[6px] border-neutral overflow-hidden bg-base-100 flex items-center justify-center shadow-md">
                        {identity?.photoURL ? (
                            <img src={identity.photoURL} alt="User Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <FaUserCircle className="w-full h-full text-neutral-300" />
                        )}
                    </div>

                    {/* Profile Info */}
                    <div className="text-center mt-2 w-full">
                        <h2 className="text-xl font-bold tracking-tight mb-1">{fullName || translate("common.auth.unknown-user")}</h2>
                        <p className="text-neutral-content text-sm mb-6 pb-6 border-b border-neutral-content/20">{user?.email || translate("common.auth.no-email-provided")}</p>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex justify-between w-full px-2 mb-8 text-center">
                        <div className="flex flex-col">
                            <span className="font-bold text-lg">---</span>
                            <span className="text-xs text-neutral-content uppercase tracking-wider font-semibold">{translate("app.content")}</span>
                        </div>
                        <div className="w-px h-8 bg-neutral-content/20 self-center"></div>
                        <div className="flex flex-col">
                            <span className="font-bold text-lg">{user?.phone_number || '---'}</span>
                            <span className="text-xs text-neutral-content uppercase tracking-wider font-semibold">{translate("page.config.mobile-phone")}</span>
                        </div>
                        <div className="w-px h-8 bg-neutral-content/20 self-center"></div>
                        <div className="flex flex-col">
                            <span className="font-bold text-lg">{planOrMembershipValue || 'N/A'}</span>
                            <span className="text-xs text-neutral-content uppercase tracking-wider font-semibold">{translate("app.user")}</span>
                        </div>
                    </div>

                    <button
                        onClick={onGoogleConnect}
                        className="btn btn-primary w-full py-4 h-auto rounded-box font-bold flex items-center justify-center gap-2 mb-2"
                    >
                        {googleAuthenticated ? <FaCheckCircle /> : <FaExclamationCircle />}
                        {googleAuthenticated ? translate("common.google.refresh-connect") : translate("common.google.new-connect")}
                    </button>
                    <p className="text-[10px] text-neutral-content uppercase tracking-wider mt-2 mb-2">{translate("page.config.google-status")}</p>

                </motion.div>

                {/* Earnings / Balance Card */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="card bg-neutral text-neutral-content rounded-box shadow-xl relative mt-2 p-6"
                >
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg">{translate("page.config.earning")}</h3>
                        <FaCog className="text-neutral-content cursor-pointer hover:text-white transition-colors" />
                    </div>

                    <div className="bg-neutral-focus dark:bg-neutral/50 rounded-box p-6 flex flex-col items-center justify-center mb-6 border border-neutral-content/5">
                        <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center mb-3">
                            <FaWallet className="w-5 h-5" />
                        </div>
                        <span className="text-3xl font-bold tracking-tight text-primary mb-2">${balance || 0}</span>
                        <span className="text-xs text-neutral-content font-medium tracking-wide">{translate("page.config.earning-month")}</span>
                    </div>

                    <p className="text-center text-xs text-neutral-content leading-relaxed mb-6 font-medium">
                        {translate("page.config.update-payment-method")}
                    </p>

                    <button className="btn btn-primary btn-outline w-full py-4 h-auto rounded-box font-bold">
                        {translate("page.config.withdraw-earning")}
                    </button>
                </motion.div>

            </div>

        </div>
    );
};
